import { Injectable } from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private escapableElement: boolean | undefined;
  private inADialog: boolean | undefined;
  chromeExtConfigPopupExpanded: boolean | undefined;

  constructor() {}

  static emailValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const email = control.value;
      const responseFail = { invalid: true };

      if (!this.isValidEmail(email)) {
        return responseFail;
      }

      return null;
    };
  }

  static isValidEmail(email: string | undefined): boolean {
    if (!email) {
      return false;
    }

    const EMAIL_REGEXP =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return EMAIL_REGEXP.test(email);
  }

  /**
   * Returns a deep copy of the object
   * https://stackoverflow.com/questions/36124363/deep-copying-objects-in-angular
   */
  static deepCopy(oldObj: any) {
    let newObj = oldObj;
    if (oldObj && typeof oldObj === 'object') {
      if (oldObj instanceof Date) {
        return new Date(oldObj.getTime());
      }
      newObj = Object.prototype.toString.call(oldObj) === '[object Array]' ? [] : {};
      // tslint:disable-next-line:forin
      for (const i in oldObj) {
        newObj[i] = this.deepCopy(oldObj[i]);
      }
    }
    return newObj;
  }

  static fileNameToMimeType(fileName: string): string {
    const extension = fileName.split('.').pop();
    const defaultType = 'text/plain';
    return UtilsService.extensionToMime(extension ?? '') ?? defaultType;
  }

  static extensionToMime(extension: string): string | undefined {
    const extensionsMap: Record<string, string> = {
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      rtf: 'application/rtf',
      txt: 'text/plain',
      html: 'text/html',
      htm: 'text/html',
      csv: 'text/csv',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif'
    };

    return extensionsMap[extension];
  }

  static capitaliseFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  static lowerCaseFirstLetter(str: string): string {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  /**
   * Downloads a file using native browser APIs
   * @param blob The Blob or File to download
   * @param filename The name to save the file as
   */
  static downloadFile(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();

    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  /**
   * Returns if an element is active that the Esc key could interact, e.g. an clearable input/dropdown menu/autocomplete etc.
   * Note: Only handles 1 instance. E.g. an Autocomplete clears the popup list and input content hitting Esc once.
   */
  escapableElementActive(open?: boolean): boolean {
    if (open !== undefined) {
      this.escapableElement = open;
    }
    return <boolean>this.escapableElement;
  }

  dialogOpen(open?: boolean): boolean {
    if (open !== undefined) {
      this.inADialog = open;
    }
    return <boolean>this.inADialog;
  }
}
